import { useDispatch, useSelector } from "react-redux";
import AppHeader from "../app-header";
import { USER_CONSTANTS } from "@src/constants/UiConstants";
import Logout from "@src/assets/logout.svg";
import PersonIcon from "@src/assets/person_icon.svg";
import { Download } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  setSelectedDropdownItem,
  setSelectedMenu,
  setSelectedSubMenu,
  clearHeaderState,
  setOrgDropdownMenuList,
  setSelectedOrgDropdownItem,
  setDropdownMenuList,
} from "@src/app/headersSlice";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearIntendedPath,
  clearUserDetails,
  setGlobalState,
  setNavigateWebsite,
  setParentIdByLevel,
  setTheme,
} from "@src/app/globalSlice";
import ProfileIcon from "@src/assets/profile_icon.webp";
import {
  Button,
  Icon,
  Modal,
  PopUpModal,
  Tooltip,
  Typography,
} from "top-brains-ui";

import "@src/components/header/Header.scss";
import DownloadAppModal from "@src/components/download-modal/DownloadAppModal";
import { setSelectedProject } from "@src/app/projectSlice";
import { clearTokens } from "@src/app/authSlice";
import { setSelectedSubject } from "@src/app/subjectSlice";
import Multi_roles from "@src/pages/login/multi-roles/MultiRoles";
import {
  ICON_COLOR_CONSTANTS,
  WORKSPACE_COLOR_CONSTANTS,
} from "@src/styles/ColorConstants";
import { persistor, RootState } from "@src/app/store";
import ChangePassword from "@src/pages/login/change-password/ChangePassword";
import {
  useGetAllOrganizationForSuperTaskManagerQuery,
  useGetAllOrganizationsQuery,
} from "@src/api/OrganizationApiSlice";
import { useGetCategoryQuery } from "@src/api/CategoryApiSlice";
import {
  useGetAllBatchesForStudentQuery,
  useLazyGetBatchByIdQuery,
} from "@src/api/BatchApiSlice";
import GradiantButton from "../gradiant-btn/GradiantButton";
import TopBrains from "@src/assets/top-brains.svg";
import {
  formatPrivilege,
  mapLevel,
  truncateText,
  getImageSrc,
} from "@src/utils/CommonUtils";
import {
  resetClassroomTree,
  setResourceDataForClassroom,
} from "@src/app/classroomSidebarSlice";
import {
  resetAssessmentTree,
  setResourceDataForAssessment,
} from "@src/app/AssessmentSidebarSlice";
import {
  resetPracticeQTree,
  setResourceDataForPracticeQ,
} from "@src/app/practiceQuesSidebarSlice";
import { setSelectedTab } from "@src/app/elpQuestionSlice";
import { useDropdownPagination } from "@src/hooks/useDropdownPagination";
import { appHeaderMenuItemProps } from "../app-header/types";
import { isAdmin, isOrgUser, isTrainer } from "./header.type";
import {
  useGetAllSubjectsForOrganizationQuery,
  useGetAllSubjectsQuery,
} from "@src/api/SubjectApiSlice";
import { useGetAllSyllabusForTrainerQuery } from "@src/api/SyllabusApiSlice";

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const headerData = useSelector((state: RootState) => state.headersData);
  const location = useLocation();
  const pathname = location.pathname;
  const hideSuperTrainerSubmenu = pathname.includes("subjects/organization");
  const [saOrgPage, setSaOrgPage] = useState(0);
  const [stmOrgPage, setStmOrgPage] = useState(0);
  const [batchPage, setBatchPage] = useState(0);
  const [subjectPage, setSubjectPage] = useState(0);
  const [getBatchBtyId] = useLazyGetBatchByIdQuery();

  const userDetails = useSelector(
    (state: RootState) => state.globalSlice.userDetails,
  );
  const { organizationData, selectedOrganizationName, orgLogo } = useSelector(
    (state: RootState) => state.globalSlice,
  );
  const { subjectData, subjectCategory, programmingLanguage } = useSelector(
    (state: RootState) => state.subject,
  );

  const subjectLanguages = programmingLanguage || [];
  const isSqlSubjectFromApi = subjectLanguages.some(
    (lang: string) =>
      lang &&
      (lang.toLowerCase().includes("sql") ||
        lang.toLowerCase().includes("mongo")),
  );

  const userRole = userDetails?.currentPrivilege ?? "";
  const prevUserRoleRef = useRef<string>(userRole);
  const studentId = userDetails?.id ?? "";
  const userAllRoles = userDetails?.privileges ?? [];
  const organizationId = userDetails?.organizationId;

  const isSuperRole = (userRole: string) => {
    return (
      userRole === USER_CONSTANTS.SUPER_ADMIN ||
      userRole === USER_CONSTANTS.SUPER_TASK_MANAGER ||
      userRole === USER_CONSTANTS.SUPER_ADDER ||
      userRole === USER_CONSTANTS.SUPER_VERIFIER ||
      userRole === USER_CONSTANTS.STUDENT
    );
  };
  const adminRoutes: Record<string, string> = {
    SUB_ORG: "organizations/sub-organization/branch",
    CLIENT: "organizations/clients/client-dashboard",
    ORG: "organizations/sub-organization",
    BRANCH: "organizations/branch/batch",
  };

  const shouldHideSubjectDetails =
    userDetails?.organizationType === "SUB_ORGANIZATION" ||
    userDetails?.organizationType === "BRANCH" ||
    userDetails?.organizationType === "CLIENT";

  const adminPath =
    userRole === USER_CONSTANTS.ADMIN
      ? (adminRoutes[mapLevel(userDetails.organizationType)] ?? "")
      : "";

  const isWordzappVisibleForTrainer = (() => {
    if (userRole === USER_CONSTANTS.TRAINER) {
      const orgName =
        organizationData?.name || userDetails?.organizationName || "";
      return Boolean(orgName && orgName.toLowerCase().includes("zupfly"));
    }
    if (userRole === USER_CONSTANTS.SUPER_TRAINER) {
      const orgName = organizationData?.name || "";
      const isZupfly = Boolean(
        orgName && orgName.toLowerCase().includes("zupfly"),
      );
      const isInsideOrg = !pathname.includes("subjects/organization");
      return Boolean(isZupfly && isInsideOrg);
    }
    return false;
  })();

  const orgTitle = (() => {
    if (userRole === USER_CONSTANTS.STUDENT) {
      return selectedOrganizationName;
    }

    if (isSuperRole(userDetails?.currentPrivilege)) {
      return organizationData?.name || userDetails?.organizationName;
    }

    return userDetails?.organizationName;
  })();

  const orgIcon =
    orgLogo ||
    (isSuperRole(userDetails?.currentPrivilege)
      ? organizationData && "name" in organizationData
        ? organizationData?.logo
        : userDetails?.logo
      : userDetails?.logo);

  const logoElement = orgIcon ? (
    <Tooltip title={orgTitle}>
      <img
        src={getImageSrc(orgIcon)}
        alt="Logo"
        style={{
          height: "32px",
          borderRadius: "50%",
          width: "32px",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
        }}
      />
    </Tooltip>
  ) : (
    <Tooltip title={orgTitle}>
      <div
        style={{
          height: "32px",
          width: "32px",
          borderRadius: "50%",
          backgroundColor: "white",
          color: "#434db8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "bold",
          fontSize: "16px",
          textTransform: "uppercase",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
        }}
      >
        {orgTitle?.[0]}
      </div>
    </Tooltip>
  );

  const { data: saOrgData, isFetching: saOrgLoading } =
    useGetAllOrganizationsQuery(
      {
        pageNo: saOrgPage,
        pageSize: 20,
        type: "ORGANIZATION",
      },
      { skip: userRole !== USER_CONSTANTS.SUPER_ADMIN },
    );

  const { data: stmOrgData, isFetching: stmLoading } =
    useGetAllOrganizationForSuperTaskManagerQuery(
      {
        pageNo: stmOrgPage,
        pageSize: 20,
      },
      { skip: userRole !== USER_CONSTANTS.SUPER_TASK_MANAGER },
    );

  const { data: batchData, isFetching: batchLoading } =
    useGetAllBatchesForStudentQuery(
      {
        studentId,
        pageNo: batchPage,
        pageSize: 20,
      },
      { skip: userRole !== USER_CONSTANTS.STUDENT },
    );

  const { data: adminSubjectsData, isFetching: adminSubjectsLoading } =
    useGetAllSubjectsQuery(
      {
        pageNo: subjectPage,
        pageSize: 20,
        userEmail: userDetails?.email,
      },
      {
        skip: !isAdmin(userRole),
      },
    );

  const { data: trainerSubjectsData, isFetching: trainerSubjectsLoading } =
    useGetAllSyllabusForTrainerQuery(
      {
        pageNo: subjectPage,
        pageSize: 10,
      },
      {
        skip: !isTrainer(userRole),
      },
    );

  const { data: orgSubjectsData, isFetching: orgSubjectsLoading } =
    useGetAllSubjectsForOrganizationQuery(
      {
        organizationId: organizationId,
      },
      {
        skip: !isOrgUser(userRole),
      },
    );
  const orgData =
    userRole === USER_CONSTANTS.SUPER_TASK_MANAGER ? stmOrgData : saOrgData;
  const orgLoading =
    userRole === USER_CONSTANTS.SUPER_TASK_MANAGER ? stmLoading : saOrgLoading;
  const subjectApiData = isAdmin(userRole)
    ? adminSubjectsData
    : isTrainer(userRole)
      ? trainerSubjectsData
      : orgSubjectsData;

  const subjectLoading = isAdmin(userRole)
    ? adminSubjectsLoading
    : isTrainer(userRole)
      ? trainerSubjectsLoading
      : orgSubjectsLoading;

  const {
    list: orgList,
    hasMore: orgHasMore,
    reset: resetOrgPagination,
  } = useDropdownPagination({
    data: orgData,
    pageSize: 20,
  });
  const { list: batchList, hasMore: batchHasMore } = useDropdownPagination({
    data: batchData,
    pageSize: 20,
  });

  const handleOrgLoadMore = () => {
    if (!orgLoading && orgHasMore) {
      if (userRole === USER_CONSTANTS.SUPER_TASK_MANAGER) {
        setStmOrgPage((prev) => prev + 1);
      } else {
        setSaOrgPage((prev) => prev + 1);
      }
    }
  };
  const handleBatchLoadMore = () => {
    if (!batchLoading && batchHasMore) {
      setBatchPage((prev) => prev + 1);
    }
  };

  const { data: categories, isLoading: categoryLoading } = useGetCategoryQuery(
    undefined,
    {
      skip: userRole !== USER_CONSTANTS.SUPER_TASK_MANAGER,
    },
  );

  useEffect(() => {
    if (!orgList) return;

    let dropdown = [{ label: "Organizations", value: "organizations" }];

    if (orgList?.length > 0) {
      const mapped = orgList?.map((org: any) => ({
        label: org.name,
        value: org.name,
        ...org,
      }));

      dropdown = [...dropdown, ...mapped];
    }

    dispatch(setOrgDropdownMenuList(dropdown));
    if (!organizationData) {
      dispatch(
        setSelectedOrgDropdownItem({
          label: "Organizations",
          value: "organizations",
          iconName: " ",
          id: "",
        }),
      );
    }
  }, [orgList]);

  useEffect(() => {
    if (userRole !== USER_CONSTANTS.STUDENT) return;
    if (!batchList) return;

    let dropdown = [{ label: "All Batches", value: "batches" }];

    if (batchList?.length > 0) {
      const mapped = batchList?.map((batch: any) => ({
        label: batch?.batchCode,
        value: batch?.batchCode,
        ...batch,
      }));

      dropdown = [...dropdown, ...mapped];
    }

    dispatch(setDropdownMenuList(dropdown));
  }, [batchList, userRole]);

  useEffect(() => {
    if (userRole !== USER_CONSTANTS.SUPER_TASK_MANAGER) return;

    let dropdown = [{ label: "Challenges", value: "challenges" }];

    dispatch(
      setSelectedDropdownItem({
        label: "Challenges",
        value: "challenges",
        iconName: " ",
        id: "",
      }),
    );

    if (categories?.response) {
      const mapped = categories?.response?.map((category: any) => ({
        label: category?.name,
        value: category?.name,
        ...category,
      }));

      dropdown = [...dropdown, ...mapped];
    }

    dispatch(setDropdownMenuList(dropdown));
    if (selectedMenu === "organizations") {
      dispatch(
        setSelectedDropdownItem({
          label: "Challenges",
          value: "challenges",
          iconName: " ",
          id: "",
        }),
      );
    }
  }, [categories, userRole]);

  useEffect(() => {
    if (prevUserRoleRef.current !== userRole) {
      setSaOrgPage(0);
      setStmOrgPage(0);
      resetOrgPagination();
      setSubjectPage(0);
      resetSubjectPagination();
      prevUserRoleRef.current = userRole;
    }
  }, [userRole]);
  const {
    list: subjectDropdownList,
    hasMore: subjectHasMore,
    reset: resetSubjectPagination,
  } = useDropdownPagination({
    data: subjectApiData,
    pageSize: 20,
    dataExtractor: (data) => {
      if (data?.response?.content) return data.response.content;
      if (Array.isArray(data?.response)) return data.response;
      if (Array.isArray(data)) return data;
      return [];
    },
  });

  const handleSubjectLoadMore = () => {
    if (!subjectLoading && subjectHasMore) {
      setSubjectPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (userRole === USER_CONSTANTS.SUPER_TASK_MANAGER) return;
    if (userRole === USER_CONSTANTS.STUDENT) return;

    let dropdown = [{ label: "All Subjects", value: "subjects" }];

    if (
      userRole === USER_CONSTANTS.ADMIN &&
      !orgSubjectsLoading &&
      orgSubjectsData
    ) {
      const mapped = orgSubjectsData?.response?.map((item: any) => {
        return {
          label: item?.name,
          value: item?.name,
          ...item,
        };
      });
      dropdown = [...dropdown, ...mapped];
    } else if (subjectDropdownList?.length > 0) {
      const mapped = subjectDropdownList?.map((item: any) => {
        if (
          isTrainer(userRole) ||
          userRole === USER_CONSTANTS.QUESTION_ADDER ||
          userRole === USER_CONSTANTS.QUESTION_VERIFIER
        ) {
          return {
            label: item?.subject?.name,
            value: item?.subject?.name,
            ...item,
          };
        }
        return {
          label: item?.name,
          value: item?.name,
          ...item,
        };
      });

      dropdown = [...dropdown, ...mapped];
    }

    dispatch(setDropdownMenuList(dropdown));
  }, [subjectDropdownList, userRole, orgSubjectsData]);

  const [changePasswordDrawerOpen, setChangePasswordDrawerOpen] =
    useState(false);

  const {
    menuDropdownList,
    orgMenuDropdownList,
    selectedMenu,
    selectedDropdownItem,
    selectedOrgDropdownItem,
    selectedSubMenu,
  } = headerData;

  const getActiveMenuAndSubMenu = (
    currentPath?: string,
    currentSearch?: string,
  ) => {
    const cleanPath = (currentPath || "").replace(/^\/+|\/+$/g, "");
    const firstSegment = cleanPath?.split("/")?.[0] || "";

    if (cleanPath === "student-profile") {
      return { menu: "", subMenu: "" };
    }

    if (firstSegment === "subjects") {
      let subMenu = "";
      if (
        cleanPath?.includes("subjects/overview") ||
        cleanPath?.includes("subjects/details")
      ) {
        subMenu = "Overview";
      } else if (cleanPath?.includes("subjects/syllabuses")) {
        subMenu = "Syllabus";
      } else if (cleanPath?.includes("subjects/students")) {
        subMenu = "Students";
      } else if (cleanPath?.includes("subjects/users")) {
        subMenu = "Users";
      } else if (cleanPath?.includes("subjects/tutorials")) {
        subMenu = currentSearch?.includes("mode=assessment")
          ? "Assessment"
          : "Tutorials";
      } else if (cleanPath?.includes("subjects/playground")) {
        subMenu = "Playground";
      } else if (cleanPath?.includes("subjects/objectives")) {
        subMenu = "Objectives";
      } else if (cleanPath?.includes("subjects/questions")) {
        subMenu = "Questions";
      } else if (cleanPath?.includes("subjects/shared-question")) {
        subMenu = "Shared Question";
      } else if (cleanPath?.includes("subjects/database-management")) {
        subMenu = "Databases";
      } else if (cleanPath?.includes("subjects/test")) {
        subMenu = "Tests";
      } else if (cleanPath?.includes("subjects/batches")) {
        subMenu = "Batch";
      } else if (cleanPath?.includes("subjects/practice")) {
        subMenu = "Practice";
      } else if (cleanPath?.includes("subjects/organizations")) {
        subMenu = "Organizations";
      }

      return {
        menu: "subjects",
        subMenu,
      };
    }

    if (firstSegment === "users") {
      return { menu: "users", subMenu: "" };
    }

    if (firstSegment === "organizations" || firstSegment === "organization") {
      if (userRole === USER_CONSTANTS.ADMIN) {
        let subMenu = "Sub Organization";
        if (cleanPath?.includes("client")) {
          subMenu = "Client";
        } else if (cleanPath?.includes("branch")) {
          subMenu = "Branch";
        } else if (cleanPath?.includes("batch")) {
          subMenu = "Batch";
        } else if (
          cleanPath?.includes("users") ||
          cleanPath?.includes("user")
        ) {
          subMenu = "User";
        } else if (cleanPath?.includes("sub-organization")) {
          subMenu = "Sub Organization";
        }
        return {
          menu: userDetails?.organizationName || "organizations",
          subMenu,
        };
      }

      if (userRole === USER_CONSTANTS.SUPER_TASK_MANAGER) {
        let subMenu = "";
        if (
          cleanPath?.includes("quizGroup") ||
          cleanPath?.includes("quiz-group")
        ) {
          subMenu = "Quiz Group";
        } else if (cleanPath?.includes("quiz")) {
          subMenu = "Quiz";
        } else if (cleanPath?.includes("tournaments")) {
          subMenu = "Tournaments";
        }
        return {
          menu: "organizations",
          subMenu,
        };
      }

      let subMenu = "";
      if (cleanPath?.includes("sub-organization")) {
        subMenu = "Sub Organization";
      } else if (
        cleanPath?.includes("clients") ||
        cleanPath?.includes("client")
      ) {
        subMenu = "Client";
      } else if (cleanPath?.includes("branch")) {
        subMenu = "Branch";
      } else if (cleanPath?.includes("batch")) {
        subMenu = "Batch";
      } else if (cleanPath?.includes("users") || cleanPath?.includes("user")) {
        subMenu = "User";
      } else if (cleanPath?.includes("registration-approval")) {
        subMenu = "Student Approval's";
      }

      return {
        menu: "organizations",
        subMenu,
      };
    }

    if (firstSegment === "registration-approval") {
      const subMenu = cleanPath?.includes("form") ? "Form" : "Approval";
      return { menu: "Student Approval's", subMenu };
    }

    if (firstSegment === "template") {
      return { menu: "template", subMenu: "" };
    }

    if (firstSegment === "traits") {
      return { menu: "traits", subMenu: "" };
    }

    if (firstSegment === "paragraphs") {
      return { menu: "paragraphs", subMenu: "" };
    }

    if (firstSegment === "companies") {
      return { menu: "companies", subMenu: "" };
    }

    if (firstSegment === "categories") {
      let subMenu = "";
      if (cleanPath?.includes("questions")) {
        subMenu = "Questions";
      } else if (cleanPath?.includes("lists")) {
        subMenu = "Lists";
      } else if (cleanPath?.includes("topics")) {
        subMenu = "Topics";
      }
      return {
        menu: "challenges",
        subMenu,
      };
    }

    if (firstSegment === "feedback") {
      return {
        menu: "Feedback",
        subMenu: "",
      };
    }

    if (firstSegment === "batches") {
      if (cleanPath?.includes("feedback")) {
        return {
          menu: "Feedback",
          subMenu: "",
        };
      }
      let subMenu = "";
      if (cleanPath?.includes("elp")) {
        subMenu = "Classroom";
      } else if (cleanPath?.includes("workspace")) {
        subMenu = "Workspace";
      } else if (cleanPath?.includes("practicequestion")) {
        subMenu = "Practice";
      }
      return {
        menu: "batches",
        subMenu,
      };
    }

    if (firstSegment === "activities") {
      let subMenu = "My Quiz";
      if (
        cleanPath?.includes("quizGroup") ||
        cleanPath?.includes("quiz-group")
      ) {
        subMenu = "Quiz Groups";
      } else if (cleanPath?.includes("tournaments")) {
        subMenu = "Tournaments";
      } else if (cleanPath?.includes("myQuiz")) {
        subMenu = "My Quiz";
      }
      return { menu: "Activities", subMenu };
    }

    if (firstSegment === "dashboard") {
      return { menu: "Dashboard", subMenu: "" };
    }

    if (firstSegment === "admin-dashboard") {
      return {
        menu: userDetails?.organizationName || "Dashboard",
        subMenu: "",
      };
    }

    if (firstSegment === "quiz") {
      return { menu: "quiz", subMenu: "" };
    }
    if (firstSegment === "quizGroup" || firstSegment === "quiz-group") {
      return { menu: "quizGroup", subMenu: "" };
    }
    if (firstSegment === "tournaments") {
      return { menu: "Tournaments", subMenu: "" };
    }

    if (firstSegment === "wordzapp") {
      let subMenu = "Home";
      if (cleanPath?.includes("lists")) {
        subMenu = "Vocabulary Lists";
      }
      return { menu: "wordzapp", subMenu };
    }

    if (firstSegment === "placements" || firstSegment === "placement") {
      return { menu: "Placement Prep", subMenu: "" };
    }

    return {};
  };

  useEffect(() => {
    const { menu, subMenu } = getActiveMenuAndSubMenu(
      pathname,
      location?.search,
    );

    if (menu !== undefined && menu !== selectedMenu) {
      dispatch(setSelectedMenu(menu));
    }
    if (subMenu !== undefined && subMenu !== selectedSubMenu) {
      dispatch(setSelectedSubMenu(subMenu));
    }
  }, [
    pathname,
    location?.search,
    userRole,
    userDetails?.organizationName,
    selectedMenu,
    selectedSubMenu,
    dispatch,
  ]);

  const resetDropdownSelections = () => {
    if (userRole === USER_CONSTANTS.SUPER_TASK_MANAGER) {
      dispatch(
        setSelectedDropdownItem({
          label: "Challenges",
          value: "challenges",
          iconName: "",
          id: "",
        }),
      );

      dispatch(
        setSelectedOrgDropdownItem({
          label: "Organizations",
          value: "organizations",
          iconName: " ",
          id: "",
        }),
      );
    } else {
      dispatch(
        setSelectedDropdownItem({
          label: "All Subjects",
          value: "allSubjects",
          iconName: "",
          id: "",
          subjectId: "",
          syllabusId: "",
          syllabusName: "",
          batchId: "",
          category: "",
          subjectName: "",
          programmingLanguage: [],
        }),
      );

      dispatch(
        setSelectedOrgDropdownItem({
          label: "Organizations",
          value: "organizations",
          iconName: " ",
          id: "",
        }),
      );
    }
  };

  const menuDropdownClickHandler = async (
    name: string,
    item: {
      label: string;
      value: string;
      iconName: string;
      id: string;
      name: string;
      subjectId: string;
      syllabusId: string;
      syllabusName: string;
      batchId: string;
      category: string;
      organizationId?: string;
      subjectName: string;
      programmingLanguage: string[];
      subject: {
        id: string;
        programmingLanguage: string[];
        category: string;
        name: string;
      };
      classStarted?: boolean;
    },
  ) => {
    if (
      name === "users" ||
      (name === "subjects" && selectedMenu === "organizations") ||
      (name === "organizations" && selectedMenu === "subjects") ||
      (name === "organizations" && selectedMenu === "challenges") ||
      (name === "challenges" && selectedMenu === "organizations")
    ) {
      resetDropdownSelections();
    }
    if (name === "subjects") {
      dispatch(setSelectedDropdownItem(item));
    } else if (name === "organizations") {
      dispatch(setSelectedOrgDropdownItem(item));
    } else if (name === "batches") {
      dispatch(setSelectedDropdownItem(item));
    } else if (name === "challenges") {
      dispatch(setSelectedDropdownItem(item));
    }
    if (item.label === "All Subjects" || item.label === "All Batches") {
      dispatch(
        setGlobalState({
          batchId: "",
          resourceId: "",
          nodeResourceId: "",
          resourceType: "",
          organizationData:
            item.label === "All Subjects" ? null : organizationData,
          parentTreeState: {
            resourceId: "",
            resourceType: "",
          },
        }),
      );
      dispatch(
        setSelectedSubject({
          subjectId: "",
          syllabusId: "",
          syllabusName: "",
          subjectName: "",
          subjectCategory: "",
          programmingLanguage: [],
          subjectData: null,
        }),
      );
    } else {
      let isClassStarted = item.classStarted;
      if (name === "batches" && item.batchId) {
        try {
          const result = await getBatchBtyId({
            batchId: item.batchId,
          }).unwrap();
          if (result.statusCode === 200) {
            isClassStarted = result?.response?.classStarted;
          }
        } catch (error) {
          console.error("Failed to fetch batch details", error);
        }
      }

      dispatch(
        setGlobalState({
          batchId: item.batchId,
          externalLink: item.externalLink,
          batchCode: item.batchCode,
          classStarted: isClassStarted,
          resourceId: [
            USER_CONSTANTS.TRAINER,
            USER_CONSTANTS.QUESTION_ADDER,
            USER_CONSTANTS.QUESTION_VERIFIER,
            USER_CONSTANTS.STUDENT,
          ].includes(userRole)
            ? item?.syllabusId || ""
            : item?.id || "",
          nodeResourceId:
            ([
              USER_CONSTANTS.TRAINER,
              USER_CONSTANTS.QUESTION_ADDER,
              USER_CONSTANTS.QUESTION_VERIFIER,
              USER_CONSTANTS.STUDENT,
            ].includes(userRole)
              ? item?.syllabusId
              : item.id) ?? null,
          resourceType: [
            USER_CONSTANTS.TRAINER,
            USER_CONSTANTS.QUESTION_ADDER,
            USER_CONSTANTS.QUESTION_VERIFIER,
            USER_CONSTANTS.STUDENT,
          ].includes(userRole)
            ? "SYLLABUS"
            : "SUBJECT",
          ...(userRole !== USER_CONSTANTS.STUDENT
            ? name === "organizations"
              ? {
                  organizationData: item,
                }
              : { organizationData: null }
            : { organizationData: null }),
          challengeCategoryData: item,
          parentTreeState: {
            resourceId:
              ([
                USER_CONSTANTS.TRAINER,
                USER_CONSTANTS.QUESTION_ADDER,
                USER_CONSTANTS.QUESTION_VERIFIER,
                USER_CONSTANTS.STUDENT,
              ].includes(userRole)
                ? item?.syllabusId
                : item.id) ?? null,
            resourceType: [
              USER_CONSTANTS.TRAINER,
              USER_CONSTANTS.QUESTION_ADDER,
              USER_CONSTANTS.QUESTION_VERIFIER,
              USER_CONSTANTS.SUPER_TRAINER,
              USER_CONSTANTS.STUDENT,
            ].includes(userRole)
              ? "SYLLABUS"
              : "SUBJECT",
          },
        }),
      );
      dispatch(
        setSelectedSubject({
          subjectData: item,
          subjectId: [
            USER_CONSTANTS.TRAINER,
            USER_CONSTANTS.QUESTION_ADDER,
            USER_CONSTANTS.QUESTION_VERIFIER,
          ].includes(userRole)
            ? item?.subject?.id
            : userRole === USER_CONSTANTS.STUDENT
              ? item.subjectRef || item.subjectId
              : item.id,
          syllabusId: item.syllabusId,
          syllabusName: item?.syllabusName,
          subjectName: [
            USER_CONSTANTS.TRAINER,
            USER_CONSTANTS.QUESTION_ADDER,
            USER_CONSTANTS.QUESTION_VERIFIER,
          ].includes(userRole)
            ? item?.subject?.name
            : userRole === USER_CONSTANTS.STUDENT
              ? item?.subjectName
              : item?.name,
          programmingLanguage: [
            USER_CONSTANTS.TRAINER,
            USER_CONSTANTS.QUESTION_ADDER,
            USER_CONSTANTS.QUESTION_VERIFIER,
          ].includes(userRole)
            ? item?.subject?.programmingLanguage
            : item?.programmingLanguage,
          subjectCategory: [
            USER_CONSTANTS.QUESTION_ADDER,
            USER_CONSTANTS.QUESTION_VERIFIER,
          ].includes(userRole)
            ? item?.subject?.category
            : item?.category,
        }),
      );

      if (name === "organizations") {
        const id = item?.id ?? item?.organizationId;

        if (!id) {
          console.error("Organization ID missing");
          return;
        }
        dispatch(
          setParentIdByLevel({
            level: "ORG",
            id,
            name: item?.name || "",
          }),
        );
      }
    }
    dispatch(setSelectedProject(item));
    dispatch(setSelectedMenu(name));
    sessionStorage.setItem("shouldCallAssessmentApi", "true");
    sessionStorage.setItem("shouldCallElpApi", "true");
    sessionStorage.setItem("shouldCallPracticeQApi", "true");
    dispatch(resetClassroomTree());
    dispatch(resetAssessmentTree());
    dispatch(resetPracticeQTree());
    dispatch(
      setResourceDataForAssessment({
        resourceId: item.syllabusId,
        resourceType: "SYLLABUS",
      }),
    );
    dispatch(
      setResourceDataForClassroom({
        resourceId: item.syllabusId,
        resourceType: "SYLLABUS",
      }),
    );
    dispatch(
      setResourceDataForPracticeQ({
        resourceId: item.syllabusId,
        resourceType: "SYLLABUS",
      }),
    );

    switch (userRole) {
      case USER_CONSTANTS.SUPER_USER:
        if (item.label === "All Subjects") {
          navigate(`/subjects`);
        } else {
          navigate(`/subjects/details`);
        }
        break;
      case USER_CONSTANTS.SUPER_ADMIN:
        if (name === "subjects") {
          if (item.label === "All Subjects") {
            navigate(`/subjects`);
          } else {
            dispatch(setSelectedSubMenu("Overview"));
            navigate(`/subjects/details`);
          }
        } else if (name === "organizations") {
          if (item.label === "Organizations") {
            navigate("/organizations");
          } else {
            dispatch(setSelectedSubMenu("Sub Organization"));
            navigate("/organizations/sub-organization");
          }
        }
        break;

      case USER_CONSTANTS.ADMIN:
        if (item.label === "All Subjects") {
          navigate(`/subjects`);
        } else {
          dispatch(setSelectedSubMenu("Syllabus"));
          navigate(`/subjects/syllabuses`);
        }
        break;
      case USER_CONSTANTS.SUBJECT_OWNER:
        if (item.label === "All Subjects") {
          navigate(`/subjects`);
        } else {
          navigate(`/subjects/details`);
        }
        break;
      case USER_CONSTANTS.QUESTION_ADDER:
        if (item.label === "All Subjects") {
          navigate(`/subjects`);
        } else {
          dispatch(setSelectedSubMenu("Questions"));
          navigate(`/subjects/questions`);
        }
        break;
      case USER_CONSTANTS.SUPER_ADDER:
        if (item.label === "All Subjects") {
          navigate(`/subjects`);
        } else {
          dispatch(setSelectedSubMenu("Questions"));
          navigate(`/subjects/questions`);
        }
        break;
      case USER_CONSTANTS.SUPER_VERIFIER:
        if (item.label === "All Subjects") {
          navigate(`/subjects`);
        } else {
          dispatch(setSelectedSubMenu("Overview"));
          navigate(`/subjects/details`);
        }
        break;
      case USER_CONSTANTS.QUESTION_VERIFIER:
        if (item.label === "All Subjects") {
          navigate(`/subjects`);
        } else {
          dispatch(setSelectedSubMenu("Questions"));
          navigate(`/subjects/questions`);
        }
        break;
      case USER_CONSTANTS.TRAINER:
        if (item.label === "All Subjects") {
          navigate(`/subjects`);
        } else {
          dispatch(setSelectedSubMenu("Batch"));
          navigate(`/subjects/batches`);
        }
        break;
      case USER_CONSTANTS.SUPER_TRAINER:
        if (item.label === "All Subjects") {
          navigate(`/subjects`);
        } else {
          navigate(`/subjects/organizations`);
        }
        break;
      case USER_CONSTANTS.STUDENT:
        if (item.label === "All Batches") {
          navigate(`/batches`);
        } else {
          dispatch(setSelectedSubMenu("Classroom"));
          dispatch(setSelectedTab("Test"));
          navigate(`/batches/elp`);
        }
        break;
      case USER_CONSTANTS.SUPER_TASK_MANAGER:
        if (name === "organizations") {
          if (item.label === "Organizations") {
            navigate(`/organizations`);
          } else {
            dispatch(setSelectedSubMenu("Quiz"));
            navigate(`/organizations/quiz`);
          }
        } else if (name === "challenges") {
          if (item.label === "Challenges") {
            navigate("/categories");
          } else {
            dispatch(setSelectedSubMenu("Questions"));
            navigate("/categories/questions");
          }
        }
    }
  };
  const menuDropdownLabelClickHandler = (name: string) => {
    if (name === "subjects") {
      dispatch(
        setSelectedOrgDropdownItem({
          label: "Organizations",
          value: "organizations",
          iconName: " ",
          id: "",
        }),
      );
    } else if (name === "challenges") {
      dispatch(
        setSelectedOrgDropdownItem({
          label: "Organizations",
          value: "organizations",
          iconName: " ",
          id: "",
        }),
      );
    } else if (name === "organizations") {
      if (userRole === USER_CONSTANTS.SUPER_TASK_MANAGER) {
        dispatch(
          setSelectedDropdownItem({
            label: "Challenges",
            value: "challenges",
            iconName: "",
            id: "",
          }),
        );
      } else {
        dispatch(
          setSelectedDropdownItem({
            label: "All Subjects",
            value: "allSubjects",
            iconName: "",
            id: "",
            subjectId: "",
            syllabusId: "",
            syllabusName: "",
            batchId: "",
            category: "",
            subjectName: "",
            programmingLanguage: [],
          }),
        );
      }
      dispatch(
        setSelectedOrgDropdownItem({
          label: "Organizations",
          value: "organizations",
          iconName: " ",
          id: "",
        }),
      );
    }

    if (userRole !== USER_CONSTANTS.STUDENT) {
      dispatch(
        setGlobalState({
          organizationData: null,
          selectedOrganizationName: "",
          orgLogo: "",
        }),
      );
    }

    if (name === "subjects") {
      navigate("/subjects");
    } else if (name === "organizations") {
      navigate("/organizations");
    } else if (name === "batches") {
      navigate("/batches");
    } else if (name === "challenges") {
      navigate("/categories");
    }
    dispatch(setSelectedMenu(name));
  };
  const handleSubMenuClick = (submenuLabel: {
    label: string;
    path: string;
  }) => {
    dispatch(setSelectedSubMenu(submenuLabel.label));
    navigate(`/${submenuLabel.path}`);
    if (
      [USER_CONSTANTS.TRAINER, USER_CONSTANTS.SUPER_TRAINER].includes(userRole)
    ) {
      const payload =
        userRole === USER_CONSTANTS.TRAINER
          ? {
              resourceId: subjectData?.syllabusId || "",
              resourceType: "SYLLABUS",
              parentTreeState: {
                resourceId: subjectData?.syllabusId || "",
                resourceType: "SYLLABUS",
              },
            }
          : {
              nodeResourceId: subjectData.id,
              resourceId: organizationData?.syllabusId || "",
              resourceType: "SYLLABUS",
              parentTreeState: {
                resourceId: organizationData?.syllabusId || "",
                resourceType: "SYLLABUS",
              },
            };
      dispatch(setGlobalState(payload));

      const subjectPayload =
        userRole === USER_CONSTANTS.TRAINER
          ? {
              syllabusId: subjectData?.syllabusId || "",
              subjectName: subjectData?.subject?.name || "",
              subjectCategory: subjectData?.subject?.category || "",
              subjectId: subjectData?.subject?.id || "",
              programmingLanguage:
                subjectData?.subject?.programmingLanguage || [],
            }
          : {
              subjectId: subjectData.id,
              subjectCategory: subjectData?.category || "",
              subjectName: subjectData?.name || "",
              programmingLanguage: subjectData?.programmingLanguage,
              syllabusId: organizationData?.syllabusId || "",
            };
      dispatch(setSelectedSubject(subjectPayload));
    }
  };

  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
    }
    if (showProfileDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileDropdown]);

  const handleProfileClick = () => {
    setShowProfileDropdown((prev) => !prev);
  };

  const headerRightSideContent = (
    <div className="header-right-content">
      <div className="right-profile-container">
        {userRole === USER_CONSTANTS.STUDENT && (
          <>
            <div
              onClick={() => setIsDownloadModalOpen(true)}
              className="download-app-icon"
            >
              <Tooltip title="Download CALIBRATE">
                <Download size={20} color="#ffffffb6" />
              </Tooltip>
            </div>
            <div className="profile-separator"></div>
          </>
        )}
        <GradiantButton
          label="Top Brains"
          height="28px"
          width="102px"
          className="home-button"
          img={TopBrains}
          labelClassName="home-button-text"
          onClick={() => {
            dispatch(setNavigateWebsite(true));
          }}
        />
        <div className="profile-separator"></div>
        <img
          src={ProfileIcon}
          alt="Profile"
          onClick={handleProfileClick}
          loading="lazy"
          height={24}
          width={24}
          className="profile-image"
        />
      </div>
    </div>
  );

  const handleProfileOptionClick = (path: string) => {
    dispatch(setSelectedMenu(""));
    navigate(path);
    setShowProfileDropdown(false);
  };

  const handleLogout = () => {
    dispatch(clearUserDetails());
    dispatch(clearHeaderState());
    dispatch(clearTokens());
    persistor.purge();
    sessionStorage.clear();
    navigate("/");
  };
  const handleMenuClick = (item: any) => {
    if (
      item.name === "subjects" ||
      item.name === "organizations" ||
      item.name === "users" ||
      item.name === "companies" ||
      item.name === "challenges" ||
      item.name === "Student Approval's" ||
      item.name === "template" ||
      item.name === "traits" ||
      item.name === "wordzapp"
    ) {
      resetDropdownSelections();
    }

    dispatch(setGlobalState({ clientSelectedTab: "Dashboard" }));

    if (
      userRole === USER_CONSTANTS.ADMIN &&
      item.name === userDetails?.organizationName
    ) {
      dispatch(setSelectedSubMenu("Sub Organization"));
      resetDropdownSelections();
    } else if (
      userRole === USER_CONSTANTS.ADMIN &&
      item.name === "Student Approval's"
    ) {
      dispatch(setSelectedSubMenu("Approval"));
    } else if (userRole === USER_CONSTANTS.STUDENT) {
      dispatch(setSelectedSubMenu("My Quiz"));
    }
    navigate(`/${item.path}`);
    dispatch(setSelectedMenu(item.label));

    if (
      userRole !== USER_CONSTANTS.STUDENT &&
      !(userRole === USER_CONSTANTS.SUPER_TRAINER && item.name === "wordzapp")
    ) {
      dispatch(
        setGlobalState({
          organizationData: null,
          selectedOrganizationName: "",
          orgLogo: "",
        }),
      );
    }
  };
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isRoleSwitchOpen, setIsRoleSwitchOpen] = useState(false);

  const handleCloseRoleSwitch = () => {
    setIsRoleSwitchOpen(false);
  };

  const handleOpenRoleSwitch = () => {
    // setIsRoleSwitchOpen(true);
    dispatch(setTheme("website-theme"));
    dispatch(clearIntendedPath());
    setShowProfileDropdown(false);
    if (userAllRoles.length > 1) {
      if (userAllRoles.includes(USER_CONSTANTS.STUDENT)) {
        navigate("/choose-role");
      } else {
        navigate("/multi_roles", { state: { defaultRole: userRole } });
      }
    }
  };

  const SUPER_ADMIN_HEADER: appHeaderMenuItemProps[] = [
    {
      name: "subjects",
      type: "dropdown",
      label: "",
      path: "/subjects",
      list: [...menuDropdownList],
      loadMore: handleSubjectLoadMore,
      hasMore: subjectHasMore,
      isLoading: subjectLoading,
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "All Subjects",
      defaultDropdownPath: "/subjects",
      selectedDropdownItem: selectedDropdownItem,
      subMenuItems: [
        {
          label: "Overview",
          path: "subjects/overview",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Users",
          path: "subjects/users",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Tutorials",
          path: "subjects/tutorials",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Playground",
          path: "subjects/playground",
          disable: false,
          disableText: "",
          hide: !subjectCategory || subjectCategory.toUpperCase() !== "BACKEND",
        },
        {
          label: "Objectives",
          path: "subjects/objectives",
          disable: false,
          disableText: "",
          hide: false,
        },
        // {
        //   label: "Videos",
        //   path: "subjects/videos",
        //   disable: false,
        //   disableText: "",
        //   hide: false,
        // },
        {
          label: "Questions",
          path: "subjects/questions",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Databases",
          path: "subjects/database-management",
          disable: false,
          disableText: "",
          hide: userRole === USER_CONSTANTS.STUDENT || !isSqlSubjectFromApi,
        },
        {
          label: "Tests",
          path: "subjects/test",
          disable: false,
          disableText: "",
          hide: false,
        },
      ],
    },
    {
      name: "users",
      type: "label",
      label: "Users",
      path: "users",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
    {
      name: "organizations",
      type: "dropdown",
      label: "",
      path: "/organizations",
      list: orgMenuDropdownList,
      loadMore: handleOrgLoadMore,
      hasMore: orgHasMore,
      isLoading: orgLoading,
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "Organizations",
      defaultDropdownPath: "/organizations",
      selectedDropdownItem: selectedOrgDropdownItem,
      subMenuItems: [
        {
          label: "Sub Organization",
          path: "organizations/sub-organization",
          disable: false,
          disableText: "",
          hide: true,
        },
        {
          label: "Client",
          path: "organizations/clients",
          disable: false,
          disableText: "",
          hide: true,
        },
        {
          label: "Branch",
          path: "organizations/branch",
          disable: false,
          disableText: "",
          hide: true,
        },

        {
          label: "Batch",
          path: "organizations/batch",
          disable: false,
          disableText: "",
          hide: true,
        },
        {
          label: "User",
          path: "organizations/users",
          disable: false,
          disableText: "",
          hide: true,
        },
        {
          label: "Student Approval's",
          path: "organizations/registration-approval",
          disable: false,
          disableText: "",
          hide: true,
        },
      ],
    },
    {
      name: "template",
      type: "label",
      label: "Templates",
      path: "template",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
    {
      name: "traits",
      type: "label",
      label: "Traits",
      path: "traits",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
    {
      name: "paragraphs",
      type: "label",
      label: "Paragraphs",
      path: "paragraphs",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
    // {
    //   name: "Placement Prep",
    //   type: "label",
    //   label: "Placement Prep",
    //   path: "placements/admin",
    //   list: [],
    //   disable: false,
    //   disableText: "",
    //   hide: false,
    //   defaultDropdownLabel: "",
    //   defaultDropdownPath: "",
    //   selectedDropdownItem: {
    //     label: "",
    //     value: "",
    //     iconName: "",
    //     id: "",
    //   },
    //   subMenuItems: [],
    // },
  ];
  const ADMIN_HEADER: appHeaderMenuItemProps[] = [
    {
      name: "subjects",
      type: "dropdown",
      label: "",
      path: "/subjects",
      list: [...menuDropdownList],
      isLoading: subjectLoading,
      disable: false,
      disableText: "",
      hide: shouldHideSubjectDetails,
      defaultDropdownLabel: "All Subjects",
      defaultDropdownPath: "/subjects",
      selectedDropdownItem: selectedDropdownItem,
      subMenuItems: [
        // {
        //   label: "Overview",
        //   path: "subjects/overview",
        //   disable: false,
        //   disableText: "",
        //   hide: false,
        // },
        {
          label: "Syllabus",
          path: "subjects/syllabuses",
          disable: false,
          disableText: "",
          hide: shouldHideSubjectDetails,
        },
        {
          label: "Users",
          path: "subjects/users",
          disable: false,
          disableText: "",
          hide: shouldHideSubjectDetails,
        },
        {
          label: "Students",
          path: "subjects/students",
          disable: false,
          disableText: "",
          hide: shouldHideSubjectDetails,
        },
        {
          label: "Questions",
          path: "subjects/questions",
          disable: false,
          disableText: "",
          hide: shouldHideSubjectDetails,
        },
        {
          label: "Databases",
          path: "subjects/database-management",
          disable: false,
          disableText: "",
          hide: userRole === USER_CONSTANTS.STUDENT || !isSqlSubjectFromApi,
        },
        {
          label: "Tests",
          path: "subjects/test",
          disable: false,
          disableText: "",
          hide: shouldHideSubjectDetails,
        },
      ],
    },
    {
      name: userDetails?.organizationName,
      type: "label",
      label: userDetails?.organizationName,
      path: adminPath,
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [
        {
          label: "Sub Organization",
          path: "organization/sub-organization",
          disable: false,
          disableText: "",
          hide: true,
        },
        {
          label: "Client",
          path: "organization/clients",
          disable: false,
          disableText: "",
          hide: true,
        },
        {
          label: "Branch",
          path: "organization/branch",
          disable: false,
          disableText: "",
          hide: true,
        },

        {
          label: "Batch",
          path: "organization/batch",
          disable: false,
          disableText: "",
          hide: true,
        },
        {
          label: "User",
          path: "organization/users",
          disable: false,
          disableText: "",
          hide: true,
        },
      ],
    },
    {
      name: "Student Approval's",
      type: "label",
      label: "Student Approval's",
      path: "registration-approval",
      list: [],
      disable: false,
      disableText: "",
      hide: true,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [
        {
          label: "Approval",
          path: "registration-approval",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Form",
          path: "registration-approval/form",
          disable: false,
          disableText: "",
          hide: false,
        },
      ],
    },
    {
      name: "paragraphs",
      type: "label",
      label: "Paragraphs",
      path: "paragraphs",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
    // {
    //   name: "Placement Prep",
    //   type: "label",
    //   label: "Placement Prep",
    //   path: "placements/admin",
    //   list: [],
    //   disable: false,
    //   disableText: "",
    //   hide: false,
    //   defaultDropdownLabel: "",
    //   defaultDropdownPath: "",
    //   selectedDropdownItem: {
    //     label: "",
    //     value: "",
    //     iconName: "",
    //     id: "",
    //   },
    //   subMenuItems: [],
    // },
  ];
  const QUESTION_ADDER_HEADER: appHeaderMenuItemProps[] = [
    {
      name: "subjects",
      type: "dropdown",
      label: "",
      path: "/subjects",
      list: [...menuDropdownList],
      loadMore: handleSubjectLoadMore,
      hasMore: subjectHasMore,
      isLoading: subjectLoading,
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "All Subjects",
      defaultDropdownPath: "/subjects",
      selectedDropdownItem: selectedDropdownItem,
      subMenuItems: [
        {
          label: "Questions",
          path: "subjects/questions",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Tests",
          path: "subjects/test",
          disable: false,
          disableText: "",
          hide: false,
        },
      ],
    },
    {
      name: "paragraphs",
      type: "label",
      label: "Paragraphs",
      path: "paragraphs",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
  ];
  const QUESTION_VERIFIER_HEADER: appHeaderMenuItemProps[] = [
    {
      name: "subjects",
      type: "dropdown",
      label: "",
      path: "/subjects",
      list: [...menuDropdownList],
      loadMore: handleSubjectLoadMore,
      hasMore: subjectHasMore,
      isLoading: subjectLoading,
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "All Subjects",
      defaultDropdownPath: "/subjects",
      selectedDropdownItem: selectedDropdownItem,
      subMenuItems: [
        {
          label: "Overview",
          path: "subjects/overview",
          disable: false,
          disableText: "",
          hide: userRole === USER_CONSTANTS.QUESTION_VERIFIER ? true : false,
        },
        {
          label: "Questions",
          path: "subjects/questions",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Tests",
          path: "subjects/test",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Shared Question",
          path: "subjects/shared-question",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Tutorials",
          path: "subjects/tutorials",
          disable: false,
          disableText: "",
          hide: userRole === USER_CONSTANTS.QUESTION_VERIFIER ? true : false,
        },
        {
          label: "Assessment",
          path: "subjects/tutorials?mode=assessment",
          disable: false,
          disableText: "",
          hide:
            userRole === USER_CONSTANTS.QUESTION_VERIFIER ||
            subjectCategory?.toUpperCase() !== "DEVOPS",
        },
      ],
    },
    {
      name: "paragraphs",
      type: "label",
      label: "Paragraphs",
      path: "paragraphs",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
  ];
  const TRAINER_HEADER: appHeaderMenuItemProps[] = [
    {
      name: "subjects",
      type: "dropdown",
      label: "",
      path: "/subjects",
      list: [...menuDropdownList],
      loadMore: handleSubjectLoadMore,
      hasMore: subjectHasMore,
      isLoading: subjectLoading,
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "All Subjects",
      defaultDropdownPath: "/subjects",
      selectedDropdownItem: selectedDropdownItem,
      subMenuItems: [
        {
          label: "Batch",
          path: "subjects/batches",
          disable: false,
          disableText: "",
          hide:
            userRole === USER_CONSTANTS.TRAINER
              ? false
              : hideSuperTrainerSubmenu,
        },
        {
          label: "Questions",
          path: "subjects/questions",
          disable: false,
          disableText: "",
          hide:
            userRole === USER_CONSTANTS.TRAINER
              ? false
              : hideSuperTrainerSubmenu,
        },
        {
          label: "Practice",
          path: "subjects/practice",
          disable: false,
          disableText: "",
          hide:
            userRole === USER_CONSTANTS.TRAINER
              ? false
              : hideSuperTrainerSubmenu,
        },
      ],
    },
    {
      name: "wordzapp",
      type: "label",
      label: "WordzApp",
      path: "wordzapp",
      disable: false,
      disableText: "",
      hide: !isWordzappVisibleForTrainer,
      subMenuItems: [
        {
          label: "Home",
          path: "wordzapp",
          disable: false,
          disableText: "",
          hide: !isWordzappVisibleForTrainer,
        },
        {
          label: "Vocabulary Lists",
          path: "wordzapp/lists",
          disable: false,
          disableText: "",
          hide: !isWordzappVisibleForTrainer,
        },
      ],
    },
    {
      name: "paragraphs",
      type: "label",
      label: "Paragraphs",
      path: "paragraphs",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
    // {
    //   name: "Placement Prep",
    //   type: "label",
    //   label: "Placement Prep",
    //   path: "placements/admin",
    //   list: [],
    //   disable: false,
    //   disableText: "",
    //   hide: false,
    //   defaultDropdownLabel: "",
    //   defaultDropdownPath: "",
    //   selectedDropdownItem: {
    //     label: "",
    //     value: "",
    //     iconName: "",
    //     id: "",
    //   },
    //   subMenuItems: [],
    // },
  ];
  const STUDENT_HEADER: appHeaderMenuItemProps[] = [
    {
      name: "batches",
      type: "dropdown",
      label: "",
      path: "/batches",
      list: menuDropdownList,
      loadMore: handleBatchLoadMore,
      hasMore: batchHasMore,
      isLoading: batchLoading,
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "All Batches",
      defaultDropdownPath: "/batches",
      selectedDropdownItem: selectedDropdownItem,
      subMenuItems: [
        // {
        //   label: "Test",
        //   path: "batches/test",
        //   disable: false,
        //   disableText: "",
        //   hide: false,
        // },
        {
          label: "Classroom",
          path: "batches/elp",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Workspace",
          path: "batches/workspace",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Practice",
          path: "batches/practicequestion",
          disable: false,
          disableText: "",
          hide: false,
        },
      ],
    },
    {
      name: "Activities",
      type: "label",
      label: "Activities",
      path: "activities/myQuiz",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [
        {
          label: "My Quiz",
          path: "activities/myQuiz",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Quiz Groups",
          path: "activities/quizGroup",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Tournaments",
          path: "activities/tournaments",
          disable: false,
          disableText: "",
          hide: false,
        },
      ],
    },
    {
      name: "Feedback",
      type: "label",
      label: "Feedback",
      path: "feedback",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
    {
      name: "Dashboard",
      type: "label",
      label: "Dashboard",
      path: "dashboard",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
    // {
    //   name: "Placement Prep",
    //   type: "label",
    //   label: "Placement Prep",
    //   path: "placements",
    //   list: [],
    //   disable: false,
    //   disableText: "",
    //   hide: false,
    //   defaultDropdownLabel: "",
    //   defaultDropdownPath: "",
    //   selectedDropdownItem: {
    //     label: "",
    //     value: "",
    //     iconName: "",
    //     id: "",
    //   },
    //   subMenuItems: [],
    // },
  ];
  const TASK_MANAGER_HEADER: appHeaderMenuItemProps[] = [
    {
      name: "quiz",
      type: "label",
      label: "Quiz",
      path: "quiz",
      disable: false,
      disableText: "",
      hide: false,
    },
    {
      name: "quizGroup",
      type: "label",
      label: "Quiz Groups",
      path: "quizGroup",
      disable: false,
      disableText: "",
      hide: false,
    },
    {
      name: "Tournaments",
      type: "label",
      label: "Tournaments",
      path: "tournaments",
      disable: false,
      disableText: "",
      hide: false,
    },
  ];

  const SUPER_TASK_MANAGER_HEADER: appHeaderMenuItemProps[] = [
    {
      name: "organizations",
      type: "dropdown",
      label: "",
      path: "/organizations",
      list: orgMenuDropdownList,
      loadMore: handleOrgLoadMore,
      hasMore: orgHasMore,
      isLoading: orgLoading,
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "Organizations",
      defaultDropdownPath: "/organizations",
      selectedDropdownItem: selectedOrgDropdownItem,
      subMenuItems: [
        {
          label: "Quiz",
          path: "organizations/quiz",
          disable: false,
          disableText: "",
          hide: false,
        },
        ...(selectedOrgDropdownItem.organizationType === "ORGANIZATION"
          ? [
              {
                label: "Quiz Group",
                path: "organizations/quizGroup",
                disable: false,
                disableText: "",
                hide: false,
              },
            ]
          : []),
        {
          label: "Tournaments",
          path: "organizations/tournaments",
          disable: false,
          disableText: "",
          hide: false,
        },
      ],
    },
    {
      name: "companies",
      type: "label",
      label: "Companies",
      path: "companies",
      list: [],
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "",
      defaultDropdownPath: "",
      selectedDropdownItem: {
        label: "",
        value: "",
        iconName: "",
        id: "",
      },
      subMenuItems: [],
    },
    {
      name: "challenges",
      type: "dropdown",
      label: "",
      path: "/categories",
      list: [...menuDropdownList],
      isLoading: categoryLoading,
      disable: false,
      disableText: "",
      hide: false,
      defaultDropdownLabel: "Challenges",
      defaultDropdownPath: "/categories",
      selectedDropdownItem: selectedDropdownItem,
      subMenuItems: [
        {
          label: "Questions",
          path: "categories/questions",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Lists",
          path: "categories/lists",
          disable: false,
          disableText: "",
          hide: false,
        },
        {
          label: "Topics",
          path: "categories/topics",
          disable: false,
          disableText: "",
          hide: false,
        },
      ],
    },
  ];

  const getHeaderByRole = (role: string) => {
    const normalizedRole = (role || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");
    switch (normalizedRole) {
      case "SUPER_ADMIN":
        return SUPER_ADMIN_HEADER;
      case "ADMIN":
        return ADMIN_HEADER;
      case "QUESTION_ADDER":
        return QUESTION_ADDER_HEADER;
      case "QUESTION_VERIFIER":
        return QUESTION_VERIFIER_HEADER;
      case "TRAINER":
        return TRAINER_HEADER;
      case "STUDENT":
        return STUDENT_HEADER;
      case "SUPER_ADDER":
        return QUESTION_ADDER_HEADER;
      case "SUPER_VERIFIER":
        return QUESTION_VERIFIER_HEADER;
      case "TASK_MANAGER":
        return TASK_MANAGER_HEADER;
      case "SUPER_TASK_MANAGER":
        return SUPER_TASK_MANAGER_HEADER;
      case "SUPER_TRAINER":
        return TRAINER_HEADER;
      default:
        return [];
    }
  };

  return (
    <div>
      <DownloadAppModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
      />

      {/* modal for role switch */}
      <Modal
        ariaHideApp
        border="1px solid #E79B0866"
        contentLabel="modal"
        customHeight="auto"
        customWidth="960px"
        headerContent={
          <button
            className="multiRoleSwitchClose"
            title="Close the role switch modal"
          >
            <Icon
              name="close"
              onClick={handleCloseRoleSwitch}
              color={ICON_COLOR_CONSTANTS.DELETE_COLOR_ICON}
            />
          </button>
        }
        isFooterDisplayed={false}
        isHeaderDisplayed={true}
        isOpen={isRoleSwitchOpen}
        onClose={() => {}}
      >
        <Multi_roles
          handleCloseRoleSwitch={handleCloseRoleSwitch}
          defaultRole={userRole}
        />
      </Modal>
      <AppHeader
        leftContent={logoElement}
        width="100%"
        borderRadius={"8px"}
        rightContent={headerRightSideContent}
        selectedMenu={selectedMenu}
        selectedSubMenu={selectedSubMenu}
        onMenuClick={handleMenuClick}
        onSubMenuClick={handleSubMenuClick}
        onMenuDropdownClick={menuDropdownClickHandler}
        onMenuDropdownLabelClick={menuDropdownLabelClickHandler}
        appHeaderMenuItems={getHeaderByRole(userRole)}
      />
      {showProfileDropdown && (
        <div ref={profileDropdownRef} className="profile-dropdown-main">
          <div className="profile-dropdown-inner-container">
            <div
              className="profile-clickable-container"
              onClick={
                userAllRoles && userAllRoles.length > 1
                  ? handleOpenRoleSwitch
                  : undefined
              }
              style={{ borderBottom: "5px solid #f2f6fc" }}
            >
              <div
                style={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "50%",
                  backgroundColor: "#434db8",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "16px",
                  textTransform: "uppercase",
                }}
              >
                {userDetails?.name?.[0]}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography fontSize={14} fontWeight="medium" color="#344054">
                  {truncateText(userDetails?.name, 17)}
                </Typography>
                <Typography fontSize={13} color="#667085">
                  {truncateText(
                    formatPrivilege(userDetails?.currentPrivilege),
                    20,
                  )}
                </Typography>
              </div>
            </div>
            {userRole === USER_CONSTANTS.STUDENT && (
              <>
                <div
                  className="profile-clickable-container"
                  onClick={() => {
                    setChangePasswordDrawerOpen(false);
                    setShowProfileDropdown(false);
                    handleProfileOptionClick("/student-profile");
                  }}
                >
                  <img src={PersonIcon} alt="Profile Icon" />
                  <Typography>My Profile</Typography>
                </div>
              </>
            )}
            {userAllRoles && userAllRoles.length > 1 && (
              <div
                className="profile-clickable-container"
                onClick={handleOpenRoleSwitch}
              >
                <Icon
                  name="group_user"
                  color={WORKSPACE_COLOR_CONSTANTS.HEADER_TEXT_COLOR}
                  className="groupUserIcon"
                  height={20}
                  width={20}
                />
                <Typography>Switch Role</Typography>
              </div>
            )}
            <>
              <div
                className="profile-clickable-container"
                onClick={() => {
                  setChangePasswordDrawerOpen(true);
                  setShowProfileDropdown(false);
                }}
              >
                <img src={PersonIcon} alt="Profile Icon" />
                <Typography>Change Password</Typography>
              </div>
              <div
                className="profile-clickable-container"
                onClick={() => handleLogout()}
              >
                <img src={Logout} alt="Logout Icon" />
                <Typography>Logout</Typography>
              </div>
            </>
          </div>
        </div>
      )}
      <ChangePassword
        onClose={setChangePasswordDrawerOpen}
        isDrawerOpen={changePasswordDrawerOpen}
      />
    </div>
  );
};

export default Header;
